import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconButton } from '@mui/material';

import usePushDataLayerEvent from '../../../utils/google-analytics.util';
import { errorData } from '../../../utils/debug.util';
import { setProvider, showToast } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import { AssistanceInformation, IPostponedSurvey } from '../../../models/assistance.model';
import useManageStorage from '../../../hooks/useManageStorage/useManageStorage';
import { ContentLayout } from '../../../global-styles/layout';
import { getSupportedLanguages, Language } from '../../../constants';
import { BodyText, Heading4Text } from '../../../components/typography/typography';
import ControlledSlider from '../../../components/common/forms/ControlledSlider';
import ControlledInput from '../../../components/common/forms/ControlledInput';
import AlxButton from '../../../components/alx-components/button';
import { icons } from '../../../assets/icons/icons';
import { ISurveyQuestion, ISurveyResponse, SurveyResponseValue } from '../../../api/survey/survey-types';
import { getSurveyQuestions, sendSurvey } from '../../../api/survey/survey';
import { SurveyStyle } from './survey-style';

const marks = [
  {
    value: 1,
    label: '1',
  },
  {
    value: 10,
    label: '10',
  },
];

type SurveyFormValues = {
  commentary?: string;
} & Record<string, SurveyResponseValue | string | undefined>;

const Survey = () => {
  // Parametros de ruta usados para identificar y enviar la encuesta.
  const { file, intervention } = useParams<{ file: string; intervention: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['surveys', 'common']);

  // Mutacion para persistir respuestas en backend.
  const sendSurveyMutation = useMutation({
    mutationFn: (body: ISurveyResponse) => sendSurvey(body),
  });

  // Estado global y cache local necesarios para resolver navegacion al finalizar.
  const dispatch = useDispatch();
  const selectedProvider = useSelector((state: RootState) => state.general.provider);
  const queryClient = useQueryClient();
  const postponedSurvey = queryClient.getQueryData<IPostponedSurvey>(['postponedSurvey']);
  const { deleteFromStorage } = useManageStorage();

  // Carga dinamica del catalogo de preguntas.
  const { data: { data: survey, status: surveyStatus } = {}, isError: surveyQuestionsError } = useQuery({
    queryKey: ['surveyQuestions', file, intervention],
    queryFn: () => getSurveyQuestions(file ?? '', intervention ?? ''),
  });

  const surveyQuestions: ISurveyQuestion[] = survey?.preguntas ?? [];
  const provider = survey?.entidad;

  // Sincroniza la compañia de la encuesta con el estado global para tema/branding.
  useEffect(() => {
    if (provider && selectedProvider !== provider) {
      dispatch(setProvider({ provider }));
    }
  }, [dispatch, provider, selectedProvider]);

  const supportedLanguages = getSupportedLanguages(t, provider);
  const currentLanguage = i18n.language?.slice(0, 2) as Language;
  const surveyLocale: Language = (supportedLanguages[currentLanguage]?.value ?? 'es') as Language;

  const getQuestionText = (question: ISurveyQuestion) => {
    return question[surveyLocale] || question.es || '';
  };

  const assistanceData = queryClient.getQueryData<AssistanceInformation>(['assitanceInformation']);
  const surveyFromCompleted = queryClient.getQueryData<boolean>(['surveyFromCompleted']);
  const { pushDataLayerEvent } = usePushDataLayerEvent();

  // Si faltan parametros obligatorios en URL, se redirige a pantalla de datos invalidos.
  useEffect(() => {
    if (!file || !intervention) navigate('/invalid-data');
  }, [file, navigate, intervention]);

  // Si falla la carga de encuestas, se redirige a pantalla de datos invalidos.
  useEffect(() => {
    if (surveyQuestionsError) {
      navigate('/invalid-data', { replace: true });
    }
  }, [navigate, surveyQuestionsError]);

  // Formulario tipado para campos dinamicos de preguntas y comentario.
  const { control, handleSubmit } = useForm<SurveyFormValues>({
    shouldUnregister: false,
    reValidateMode: 'onChange',
    mode: 'onChange',
    shouldFocusError: true,
  });

  // Limpieza de la marca de origen para evitar redirecciones incorrectas posteriores.
  useEffect(() => {
    const clearSurveyOrigin = async () => {
      await deleteFromStorage(['surveyFromCompleted']);
    };
    clearSurveyOrigin();
  }, [deleteFromStorage, surveyFromCompleted]);

  // Cierre de flujo tras guardar: decide si volver a inicio o ir a pantalla completada.
  const finishSurvey = () => {
    const identifier = postponedSurvey?.identifier || assistanceData?.identifier;
    if (!intervention || !identifier) {
      handleReturn();
    } else {
      deleteFromStorage(['postponedSurvey']);
      navigate(`/completed/${assistanceData?.file}/${intervention}`);
    }
  };

  // Convierte los valores del form dinamico al formato de payload esperado por la API.
  const onSubmit = handleSubmit(async (data) => {
    if (file === 'mocked') {
      handleReturn();
      return;
    }

    const formData = data as Record<string, SurveyResponseValue | string | undefined>;
    const commentary = String(formData.commentary ?? '');
    const firstFiveQuestions = surveyQuestions.slice(0, 5);
    const selectedAnswers = firstFiveQuestions.map(
      (question) => formData[question.codigo] as SurveyResponseValue | undefined,
    );

    const surveyBody: ISurveyResponse = {
      questions: firstFiveQuestions
        .map((question, index) => {
          const answer = selectedAnswers[index];
          if (!answer) return null;

          return {
            code: question.codigo,
            value: answer,
          };
        })
        .filter((item): item is { code: string; value: SurveyResponseValue } => item !== null),
      observations: commentary,
      file: file ?? '',
      intervention: intervention ?? '',
    };

    sendSurveyMutation.mutate(surveyBody, {
      onSuccess: () => {
        dispatch(showToast({ message: t('surveys:survey.success'), severity: 'success', open: true }));
        pushDataLayerEvent(
          'finalizar_encuesta',
          {
            tipo_opcion1: firstFiveQuestions[0] ? getQuestionText(firstFiveQuestions[0]) : '',
            tipo_opcion2: firstFiveQuestions[1] ? getQuestionText(firstFiveQuestions[1]) : '',
            tipo_opcion3: firstFiveQuestions[2] ? getQuestionText(firstFiveQuestions[2]) : '',
            tipo_opcion4: firstFiveQuestions[3] ? getQuestionText(firstFiveQuestions[3]) : '',
            tipo_opcion5: firstFiveQuestions[4] ? getQuestionText(firstFiveQuestions[4]) : '',
            opcion_elegida1: selectedAnswers[0],
            opcion_elegida2: selectedAnswers[1],
            opcion_elegida3: selectedAnswers[2],
            opcion_elegida4: selectedAnswers[3],
            opcion_elegida5: selectedAnswers[4],
            opcion_comentario: commentary,
          },
          'encuesta',
        );
        finishSurvey();
      },
      onError: (error) => {
        errorData(error);
      },
    });
  });

  // Navegacion de retorno: mantiene contexto si la encuesta vino desde pantalla completada.
  const handleReturn = () => {
    if (surveyFromCompleted) {
      navigate(`/completed/${file}/${intervention}`);
      return;
    }
    navigate('/');
  };

  // Si ya fue enviada la encuesta, notificarlo
  useEffect(() => {
    if (surveyStatus === 208) {
      dispatch(showToast({ message: t('surveys:survey.success'), severity: 'success', open: true }));
      handleReturn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyStatus]);

  return (
    <ContentLayout>
      <SurveyStyle>
        {/* Cabecera con accion de volver y titulo de la encuesta. */}
        <div className="survey-title">
          <IconButton className="survey-title__button" aria-label="back" onClick={handleReturn}>
            <img src={icons.chevronLeftIcon} alt="" />
          </IconButton>
          <Heading4Text weight={600}>{t('surveys:survey.title')}</Heading4Text>
        </div>
        {/* Si falla la carga de preguntas, el efecto redirige a /invalid-data. */}
        {surveyQuestionsError ? (
          <BodyText className="m-t-25px">No fue posible acceder al servicio de encuestas.</BodyText>
        ) : (
          <>
            {/* Renderizado dinamico de sliders basado en el arreglo recibido desde API. */}
            {surveyQuestions?.map((question, index) => (
              <div key={question.codigo}>
                <BodyText weight={600} className="m-t-25px">
                  {getQuestionText(question)}
                </BodyText>
                <ControlledSlider
                  sliderProps={{
                    className: 'm-24px',
                    valueLabelDisplay: 'auto',
                    'aria-label': 'pretto slider',
                    'data-cy': `slider-question${index + 1}`,
                    min: 1,
                    max: 10,
                    step: 1,
                    name: question.codigo,
                    marks: marks,
                  }}
                  controllerProps={{
                    control,
                    name: question.codigo,
                    defaultValue: 5,
                    rules: {
                      required: true,
                    },
                  }}
                />
              </div>
            ))}
            {/* Campo abierto para observaciones adicionales del usuario. */}
            <ControlledInput
              inputProps={{
                className: 'm-t-25px',
                rows: 3,
                multiline: true,
                variant: 'filled',
                placeholder: t('surveys:survey.6'),
                'data-cy': 'commentary',
                inputProps: {
                  maxLength: 140,
                },
              }}
              controllerProps={{
                control,
                name: 'commentary',
              }}
            />
            {/* Accion principal para enviar la encuesta. */}
            <div className="survey-send-btn">
              <AlxButton className="m-24px m-t-25px" onClick={onSubmit} data-cy="survey-submit">
                {t('common:buttons.send')}
              </AlxButton>
            </div>
          </>
        )}
      </SurveyStyle>
    </ContentLayout>
  );
};

export default Survey;
